/**
 * Semantic dirty check for appointment edit close-confirm (CVC-36613).
 * Compares current form payload to loaded appointment without false positives
 * from shape differences (e.g. businessAppointmentType shape). Role/responsibility
 * rows include occupyTimeslot so toggles are treated as edits.
 */
import moment from 'moment'
import * as R from 'ramda'
import type { Nil } from '@pbt/pbt-ui-components'

import type {
  TimetableEvent,
  TimetableEventPersonResponsibility,
  TimetableEventPersonRole,
} from '~/types/entities/timetable'

type ComparableRole = { occupyTimeslot: boolean; person: string; role: string }
type ComparableResp = {
  occupyTimeslot: boolean
  person: string
  responsibility: string
}

const boolOccupy = (v: unknown): boolean => Boolean(v)

const compareComparableRole = (
  a: ComparableRole,
  b: ComparableRole,
): number => {
  const byRole = a.role.localeCompare(b.role)
  if (byRole !== 0) return byRole
  const byPerson = a.person.localeCompare(b.person)
  if (byPerson !== 0) return byPerson
  return Number(a.occupyTimeslot) - Number(b.occupyTimeslot)
}

const compareComparableResp = (
  a: ComparableResp,
  b: ComparableResp,
): number => {
  const byResp = a.responsibility.localeCompare(b.responsibility)
  if (byResp !== 0) return byResp
  const byPerson = a.person.localeCompare(b.person)
  if (byPerson !== 0) return byPerson
  return Number(a.occupyTimeslot) - Number(b.occupyTimeslot)
}

const normalizeFormRoles = (roles: unknown): ComparableRole[] => {
  if (!Array.isArray(roles)) return []
  return roles
    .filter((r) => r && String((r as { person?: string }).person || ''))
    .map((r) => ({
      person: String((r as { person: string }).person),
      role: String((r as { role: string }).role),
      occupyTimeslot: boolOccupy(
        (r as { occupyTimeslot?: boolean }).occupyTimeslot,
      ),
    }))
    .sort(compareComparableRole)
}

const normalizeServerRoles = (
  roles: TimetableEventPersonRole[] | Nil,
): ComparableRole[] => {
  if (!roles?.length) return []
  return roles
    .filter((r) => r.personId)
    .map((r) => ({
      person: r.personId,
      role: r.roleId,
      occupyTimeslot: boolOccupy(r.occupyTimeslot),
    }))
    .sort(compareComparableRole)
}

const normalizeFormResponsibilities = (rows: unknown): ComparableResp[] => {
  if (!Array.isArray(rows)) return []
  return rows
    .filter((r) => r && String((r as { person?: string }).person || ''))
    .map((r) => ({
      person: String((r as { person: string }).person),
      responsibility: String((r as { responsibility: string }).responsibility),
      occupyTimeslot: boolOccupy(
        (r as { occupyTimeslot?: boolean }).occupyTimeslot,
      ),
    }))
    .sort(compareComparableResp)
}

const normalizeServerResponsibilities = (
  rows: TimetableEventPersonResponsibility[] | Nil,
): ComparableResp[] => {
  if (!rows?.length) return []
  return rows
    .filter((r) => r.personId)
    .map((r) => ({
      person: r.personId,
      responsibility: r.responsibilityId,
      occupyTimeslot: boolOccupy(r.occupyTimeslot),
    }))
    .sort(compareComparableResp)
}

const getNestedId = (v: unknown): string | undefined => {
  if (v && typeof v === 'object' && 'id' in v) {
    return String((v as { id: string }).id)
  }
  return undefined
}

const looseScalarEqual = (a: unknown, b: unknown): boolean => {
  if (R.equals(a, b)) return true
  if (a == null && b == null) return true
  return String(a ?? '') === String(b ?? '')
}

/**
 * Form `get()` aggregates dates with ignoreSeconds (seconds zeroed); server ISO
 * strings often include non-zero seconds — compare at minute precision in UTC.
 */
const isoDateTimesLooselyEqual = (a: unknown, b: unknown): boolean => {
  if (R.equals(a, b)) return true
  if (a == null && b == null) return true
  const ma = moment.utc(String(a ?? ''))
  const mb = moment.utc(String(b ?? ''))
  if (!ma.isValid() || !mb.isValid()) {
    return String(a ?? '') === String(b ?? '')
  }
  return ma.isSame(mb, 'minute')
}

const ORIGINAL_OMIT_KEYS = [
  'state',
  'type',
  'assignedSpace',
  'includeZoomLink',
  'personRoles',
  'personResponsibilities',
] as const

const buildOriginalComparable = (
  old: TimetableEvent | Nil,
): Record<string, unknown> => {
  if (!old) return {}
  const rest = R.omit(ORIGINAL_OMIT_KEYS, old as Record<string, unknown>)
  return {
    ...rest,
    state: old.state?.id,
    type: old.type?.id,
    spaceId: old.assignedSpace || '',
    includeZoomLink: Boolean(old.includeZoomLink),
  }
}

const typeFieldUnchanged = (newVal: unknown, old: TimetableEvent): boolean =>
  R.equals(newVal, old.businessAppointmentType?.eventTypeId) ||
  R.equals(newVal, old.type?.id)

const businessAppointmentTypeUnchanged = (
  newVal: unknown,
  oldVal: unknown,
  oldAppt: TimetableEvent,
): boolean => {
  const nid = getNestedId(newVal)
  const oid = getNestedId(oldVal)
  if (nid !== undefined && oid !== undefined) return nid === oid
  if (nid !== undefined && (oldVal == null || oid === undefined)) {
    const baseline =
      oldAppt.businessAppointmentType?.id ?? oldAppt.type?.id ?? oldAppt.typeId
    return baseline != null && String(baseline) === nid
  }
  return R.equals(newVal, oldVal)
}

const KEYS_SKIP = new Set(['id', 'bypassValidation'])

const LOOSE_STRING_KEYS = new Set([
  'clientInstructions',
  'notes',
  'internalNotes',
  'spaceId',
  'meetingLink',
  'meetingNotes',
  'dialIn',
])

const LOOSE_ISO_DATETIME_KEYS = new Set([
  'scheduledStartDatetime',
  'scheduledEndDatetime',
  'actualStartDatetime',
  'actualEndDatetime',
  'recurrenceStartDate',
  'recurrenceEndDate',
])

const fieldUnchanged = (
  key: string,
  newVal: unknown,
  orig: Record<string, unknown>,
  oldAppt: TimetableEvent,
): boolean => {
  const oldVal = orig[key]

  if (key === 'personRoles') {
    if (newVal === undefined) return true
    return R.equals(
      normalizeFormRoles(newVal),
      normalizeServerRoles(oldAppt.personRoles),
    )
  }

  if (key === 'personResponsibilities') {
    if (newVal === undefined) return true
    return R.equals(
      normalizeFormResponsibilities(newVal),
      normalizeServerResponsibilities(oldAppt.personResponsibilities),
    )
  }

  if (key === 'businessAppointmentType') {
    return businessAppointmentTypeUnchanged(newVal, oldVal, oldAppt)
  }

  if (key === 'type') {
    return typeFieldUnchanged(newVal, oldAppt)
  }

  if (key === 'state') {
    const baseline = oldAppt.state?.id ?? oldAppt.stateId
    if (baseline == null) return true
    return looseScalarEqual(newVal, baseline)
  }

  if (key === 'client') {
    const baseline = oldAppt.client ?? oldAppt.clientId
    if (baseline == null) return true
    return looseScalarEqual(newVal, baseline)
  }

  if (key === 'patient') {
    const baseline = oldAppt.patient ?? oldAppt.patientId
    if (baseline == null) return true
    return looseScalarEqual(newVal, baseline)
  }

  if (key === 'business') {
    if (oldAppt.businessId == null) return true
    return looseScalarEqual(newVal, oldAppt.businessId)
  }

  if (LOOSE_ISO_DATETIME_KEYS.has(key)) {
    return isoDateTimesLooselyEqual(newVal, oldVal)
  }

  if (LOOSE_STRING_KEYS.has(key)) {
    return looseScalarEqual(newVal, oldVal)
  }

  if (key === 'includeZoomLink') {
    return Boolean(newVal) === Boolean(oldVal)
  }

  if (key === 'recurrenceParams') {
    return R.equals(newVal ?? null, oldVal ?? null)
  }

  const toNull = (v: unknown) => (v === undefined ? null : v)
  return R.equals(toNull(newVal), toNull(oldVal))
}

/**
 * Returns true if the edited appointment differs from the server snapshot in any
 * user-relevant field (i.e. user should see unsaved-changes on close).
 */
export const hasUnsavedAppointmentEdits = (
  newAppointment: Record<string, unknown>,
  oldAppointment: TimetableEvent | Nil,
): boolean => {
  if (!oldAppointment?.id) return true

  const orig = buildOriginalComparable(oldAppointment)

  const fieldDiffers = Object.keys(newAppointment).some(
    (key) =>
      !KEYS_SKIP.has(key) &&
      !fieldUnchanged(key, newAppointment[key], orig, oldAppointment),
  )

  const oldHadRecurrence = Boolean(oldAppointment.recurrenceParams)
  const newHasRecurrence =
    'recurrenceParams' in newAppointment &&
    newAppointment.recurrenceParams != null
  const recurrenceDiffers = oldHadRecurrence !== newHasRecurrence

  return fieldDiffers || recurrenceDiffers
}
